import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useAuth0 } from "@auth0/auth0-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { questions } from "../../data/questions";
import slug from "../../utils/slug";

import Timer from "../../component/interview-tab/Timer";
import UserRecording from "../../component/interview-tab/UserRecording";

import useDraftAnswer from "../../hooks/useDraftAnswer";
import useRecordings from "../../hooks/useRecordings";
import { API_BASE } from "../../config/apiBase";

import formatDateTimeNumeric from "../../utils/formatDateTimeNumeric";

import "../../styles/pages/interview/_question-detail.scss";

const MEDIAPIPE_SELFIE_SEGMENTATION_VERSION =
    process.env.REACT_APP_MEDIAPIPE_SELFIE_SEGMENTATION_VERSION ||
    "0.1.1675465747";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconChevLeft = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M10 3L5 8l5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
const IconChevRight = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M6 3l5 5-5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);
const IconVideo = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        aria-hidden="true"
    >
        <rect
            x="1"
            y="4"
            width="11"
            height="10"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="1.4"
        />
        <path
            d="M12 7.5l4-2.5v8l-4-2.5v-3z"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
        />
    </svg>
);
const IconEdit = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M9.5 1.5l3 3L4 13H1v-3L9.5 1.5z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
        />
    </svg>
);
const IconClose = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M4 4l10 10M14 4L4 14"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);
const IconNotesOn = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M5 3m0 2a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M9 7l6 0"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
        <path
            d="M9 11l6 0"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
        <path
            d="M9 15l4 0"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
    </svg>
);
const IconNotesOff = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M7 3h10a2 2 0 0 1 2 2v10m0 4a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-14"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M11 7h4"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
        <path
            d="M9 11h2"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
        <path
            d="M9 15h4"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
        <path
            d="M3 3l18 18"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
        />
    </svg>
);
const IconBlurOff = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        aria-hidden="true"
    >
        <path d="M4 16s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1zm4-5.95a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5" />
        <path d="M2 1a2 2 0 0 0-2 2v9.5A1.5 1.5 0 0 0 1.5 14h.653a5.4 5.4 0 0 1 1.066-2H1V3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v9h-2.219c.554.654.89 1.373 1.066 2h.653a1.5 1.5 0 0 0 1.5-1.5V3a2 2 0 0 0-2-2z" />
    </svg>
);
const IconBlurOn = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        aria-hidden="true"
    >
        <path d="M1.5 1a.5.5 0 0 0-.5.5v3a.5.5 0 0 1-1 0v-3A1.5 1.5 0 0 1 1.5 0h3a.5.5 0 0 1 0 1zM11 .5a.5.5 0 0 1 .5-.5h3A1.5 1.5 0 0 1 16 1.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 1-.5-.5M.5 11a.5.5 0 0 1 .5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 1 0 1h-3A1.5 1.5 0 0 1 0 14.5v-3a.5.5 0 0 1 .5-.5m15 0a.5.5 0 0 1 .5.5v3a1.5 1.5 0 0 1-1.5 1.5h-3a.5.5 0 0 1 0-1h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 1 .5-.5" />
        <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm8-9a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
    </svg>
);
const IconArrow = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2.5 7h9M8 3.5L11.5 7 8 10.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
export default function QuestionDetail() {
    const { qid } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } =
        useAuth0();
    const [token, setToken] = useState(null);
    const [plan, setPlan] = useState("free");

    // Portal target — set after mount so document.body is guaranteed to exist
    const [portalTarget, setPortalTarget] = useState(null);
    useEffect(() => {
        setPortalTarget(document.body);
    }, []);

    // Token
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!isAuthenticated) {
                setToken(null);
                return;
            }
            try {
                const t = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    },
                });
                if (!cancelled) setToken(t);
            } catch {
                if (!cancelled) setToken(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, getAccessTokenSilently]);

    // Plan
    useEffect(() => {
        if (!token) return;
        const ac = new AbortController();
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/profile`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: ac.signal,
                });
                if (!res.ok) return;
                const data = await res.json();
                setPlan(data?.plan || "free");
            } catch {}
        })();
        return () => ac.abort();
    }, [token]);

    const isProAiPlan =
        String(plan || "")
            .toLowerCase()
            .replace(/[\s-]/g, "_") === "pro_ai";

    const all = questions ?? [];
    const current = useMemo(() => {
        if (!all.length) return null;
        return all.find((q) => slug(q.id ?? q.question) === qid) ?? all[0];
    }, [all, qid]);

    const currentSlug = useMemo(
        () => (current ? slug(current.id ?? current.question) : null),
        [current]
    );

    // Canonical redirect
    useEffect(() => {
        if (!current || !currentSlug) return;
        if (qid && qid !== currentSlug)
            navigate(`/dashboard/questions/${currentSlug}`, { replace: true });
    }, [qid, current, currentSlug, navigate]);

    // Draft
    const {
        body,
        setBody,
        saving,
        error: draftError,
        lastSavedAt,
        saveNow,
        lastSavedBody,
    } = useDraftAnswer({
        token,
        questionSlug: currentSlug,
        disableAutoSave: true,
    });

    // Recordings
    const {
        items: recordings,
        loading: recordingsLoading,
        upload: uploadingRecording,
        remove: deleteRecording,
    } = useRecordings({ token, questionSlug: currentSlug });

    // UI state
    const [showModal, setShowModal] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isEditingDraft, setIsEditingDraft] = useState(false);
    const [showPlayback, setShowPlayback] = useState(false);
    const [playbackURL, setPlaybackURL] = useState(null);
    const [showProcessing, setShowProcessing] = useState(false);
    const [processingError, setProcessingError] = useState(null);
    const [isBlurOn, setIsBlurOn] = useState(false);
    const [isBlurReady, setIsBlurReady] = useState(false);
    const [blurError, setBlurError] = useState(null);

    // Media
    const mediaRecorderRef = useRef(null);
    const recordStartRef = useRef(null);
    const streamRef = useRef(null);
    const modalVideoRef = useRef(null);
    const blurCanvasRef = useRef(null);
    const blurStreamRef = useRef(null);
    const blurSegmentationRef = useRef(null);
    const blurProcessingRef = useRef(false);
    const blurAnimationRef = useRef(null);
    const blurReadyRef = useRef(false);
    const [mediaError, setMediaError] = useState(null);

    const attachStream = useCallback((stream) => {
        const el = modalVideoRef.current;
        if (!el) return;
        try {
            el.srcObject = stream;
            el.play?.().catch(() => {});
        } catch {}
    }, []);

    const stopBlurProcessing = useCallback(() => {
        if (blurAnimationRef.current) {
            cancelAnimationFrame(blurAnimationRef.current);
            blurAnimationRef.current = null;
        }
        blurProcessingRef.current = false;
        blurStreamRef.current?.getTracks().forEach((t) => t.stop());
        blurStreamRef.current = null;
        blurReadyRef.current = false;
        setIsBlurReady(false);
    }, []);

    const stopStream = useCallback(() => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (modalVideoRef.current) modalVideoRef.current.srcObject = null;
        stopBlurProcessing();
    }, [stopBlurProcessing]);

    const ensureStream = useCallback(async () => {
        if (streamRef.current) return streamRef.current;
        if (!navigator?.mediaDevices?.getUserMedia) {
            setMediaError("Camera/microphone not supported by this browser.");
            return null;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true,
            });
            streamRef.current = stream;
            attachStream(stream);
            setMediaError(null);
            return stream;
        } catch (err) {
            setMediaError(
                err?.message || "Failed to access camera/microphone."
            );
            return null;
        }
    }, [attachStream]);

    const ensureBlurSegmentation = useCallback(async () => {
        if (blurSegmentationRef.current) return blurSegmentationRef.current;
        try {
            const module = await import("@mediapipe/selfie_segmentation");
            const selfieSegmentation = new module.SelfieSegmentation({
                locateFile: (file) =>
                    `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@${MEDIAPIPE_SELFIE_SEGMENTATION_VERSION}/${file}`,
            });
            selfieSegmentation.setOptions({
                modelSelection: 1,
                selfieMode: true,
            });
            selfieSegmentation.onResults((results) => {
                const video = modalVideoRef.current;
                const canvas = blurCanvasRef.current;
                if (!video || !canvas || !results?.segmentationMask) return;

                const width =
                    video.videoWidth || canvas.clientWidth || canvas.width;
                const height =
                    video.videoHeight || canvas.clientHeight || canvas.height;
                if (!width || !height) return;

                if (canvas.width !== width || canvas.height !== height) {
                    canvas.width = width;
                    canvas.height = height;
                }

                const ctx = canvas.getContext("2d");
                if (!ctx) return;

                const sourceImage = results.image || video;

                ctx.save();
                ctx.clearRect(0, 0, width, height);
                // MediaPipe's segmentationMask is designed to be composed using
                // `source-out`/`destination-atop` (see official background blur
                // example). This avoids "empty"/transparent frames in the
                // canvas and keeps the user visible.
                ctx.drawImage(results.segmentationMask, 0, 0, width, height);

                // 1) Draw blurred background outside the person mask.
                ctx.globalCompositeOperation = "source-out";
                ctx.filter = "blur(12px)";
                ctx.drawImage(sourceImage, 0, 0, width, height);

                // 2) Draw the original image on top, clipped by the mask.
                ctx.globalCompositeOperation = "destination-atop";
                ctx.filter = "none";
                ctx.drawImage(sourceImage, 0, 0, width, height);

                ctx.restore();

                if (!blurReadyRef.current) {
                    blurReadyRef.current = true;
                    setIsBlurReady(true);
                }
            });
            blurSegmentationRef.current = selfieSegmentation;
            setBlurError(null);
            return selfieSegmentation;
        } catch (err) {
            setBlurError(
                err?.message ||
                    "Background blur is unavailable in this browser."
            );
            return null;
        }
    }, []);

    const ensureRecordingStream = useCallback(
        async (rawStream) => {
            if (!isBlurOn || !isBlurReady) return rawStream;
            const canvas = blurCanvasRef.current;
            if (!canvas) return rawStream;
            if (blurStreamRef.current) return blurStreamRef.current;

            const canvasStream = canvas.captureStream(30);
            const audioTracks = rawStream.getAudioTracks();
            const combined = new MediaStream([
                ...canvasStream.getVideoTracks(),
                ...audioTracks,
            ]);
            blurStreamRef.current = combined;
            return combined;
        },
        [isBlurOn, isBlurReady]
    );

    useEffect(() => {
        if (!showModal) {
            stopStream();
            return;
        }
        ensureStream();
        return () => stopStream();
    }, [showModal, ensureStream, stopStream]);

    useEffect(() => {
        if (!showModal || !isBlurOn) {
            stopBlurProcessing();
            return;
        }

        let isMounted = true;
        const startBlur = async () => {
            const stream = streamRef.current || (await ensureStream());
            if (!isMounted || !stream) return;
            const video = modalVideoRef.current;
            if (!video) return;

            const segmentation = await ensureBlurSegmentation();
            if (!isMounted || !segmentation) return;

            const renderFrame = async () => {
                if (!isMounted || !isBlurOn || !segmentation) return;
                const videoEl = modalVideoRef.current;
                if (!videoEl) return;
                if (videoEl.readyState < 2 || !videoEl.videoWidth) {
                    blurAnimationRef.current =
                        requestAnimationFrame(renderFrame);
                    return;
                }
                if (blurProcessingRef.current) {
                    blurAnimationRef.current =
                        requestAnimationFrame(renderFrame);
                    return;
                }
                blurProcessingRef.current = true;
                try {
                    await segmentation.send({ image: videoEl });
                } catch (err) {
                    if (isMounted) {
                        setBlurError(
                            err?.message ||
                                "Couldn't enable background blur on this device."
                        );
                        setIsBlurOn(false);
                    }
                } finally {
                    blurProcessingRef.current = false;
                }
                blurAnimationRef.current = requestAnimationFrame(renderFrame);
            };

            blurAnimationRef.current = requestAnimationFrame(renderFrame);
        };

        startBlur();
        return () => {
            isMounted = false;
            stopBlurProcessing();
        };
    }, [
        showModal,
        isBlurOn,
        ensureStream,
        ensureBlurSegmentation,
        stopBlurProcessing,
    ]);

    // Escape key closes whichever modal is open
    useEffect(() => {
        if (!showModal && !showPlayback) return;
        const handler = (e) => {
            if (e.key !== "Escape") return;
            if (showModal && !showProcessing) safeCloseModal();
            if (showPlayback) setShowPlayback(false);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [showModal, showPlayback, showProcessing]);

    useEffect(() => {
        if (showModal || showPlayback) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        // Always clean up on unmount
        return () => {
            document.body.style.overflow = "";
        };
    }, [showModal, showPlayback]);

    const safeCloseModal = useCallback(() => {
        if (showProcessing) return;
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") mr.stop();
        setIsRecording(false);
        setIsRunning(false);
        setShowNotes(false);
        setShowModal(false);
    }, [showProcessing]);

    const goRecordings = useCallback(() => {
        setShowProcessing(false);
        setProcessingError(null);
        setShowModal(false);
        navigate("/dashboard/recordings", { replace: true });
    }, [navigate]);

    const closeProcessing = useCallback(() => {
        setShowProcessing(false);
        setProcessingError(null);
        setShowModal(false);
    }, []);

    const startRecording = useCallback(async () => {
        if (!isAuthenticated) {
            await loginWithRedirect();
            return;
        }
        if (!token || !currentSlug) return;
        const rawStream = streamRef.current || (await ensureStream());
        if (!rawStream) return;
        const stream = await ensureRecordingStream(rawStream);
        if (!stream) return;

        const chunks = [];
        const mr = new MediaRecorder(stream);
        mediaRecorderRef.current = mr;

        mr.ondataavailable = (e) => {
            if (e.data?.size > 0) chunks.push(e.data);
        };
        mr.onstop = async () => {
            setShowProcessing(true);
            setProcessingError(null);
            try {
                const blob = new Blob(chunks, { type: "video/webm" });
                const startedAt = recordStartRef.current;
                const durationMs = startedAt
                    ? Math.max(0, Date.now() - startedAt)
                    : null;
                await uploadingRecording({
                    blob,
                    filename: `${currentSlug}-${Date.now()}.webm`,
                    question_slug: currentSlug,
                    duration_ms: durationMs,
                    evaluation_style: current?.evaluationStyle,
                    evaluation_criteria: current?.evaluationCriteria
                        ? JSON.stringify(current.evaluationCriteria)
                        : null,
                });
            } catch (err) {
                setProcessingError(
                    err?.message || "Upload failed. Please try again."
                );
            }
        };

        mr.start();
        recordStartRef.current = Date.now();
        setIsRecording(true);
        setIsRunning(true);
    }, [
        isAuthenticated,
        loginWithRedirect,
        token,
        currentSlug,
        ensureStream,
        ensureRecordingStream,
        uploadingRecording,
        current,
    ]);

    const stopRecording = useCallback(() => {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== "inactive") {
            setShowProcessing(true);
            setProcessingError(null);
            mr.stop();
        }
        setIsRecording(false);
        setIsRunning(false);
    }, []);

    const toggleRecording = useCallback(() => {
        if (!isRecording) startRecording();
        else stopRecording();
    }, [isRecording, startRecording, stopRecording]);

    const step = useCallback(
        (delta) => {
            if (!current || !currentSlug) return;
            const idx = all.findIndex(
                (q) => slug(q.id ?? q.question) === currentSlug
            );
            const next = all[idx + delta];
            if (next)
                navigate(
                    `/dashboard/questions/${slug(next.id ?? next.question)}`
                );
        },
        [all, current, currentSlug, navigate]
    );

    const draftText = useMemo(
        () =>
            body
                ? body
                      .replace(/<[^>]+>/g, "")
                      .replace(/\s+/g, " ")
                      .trim()
                : "",
        [body]
    );

    const hasDraft = draftText.length > 0;
    const [draftAssistApplied, setDraftAssistApplied] = useState(false);
    const [draftAssistPrevBody, setDraftAssistPrevBody] = useState("");

    const escapeHtml = useCallback(
        (value) =>
            String(value)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;"),
        []
    );

    const buildDraftResponse = useCallback(() => {
        const style =
            typeof current?.evaluationStyle === "string" &&
            current.evaluationStyle
                ? current.evaluationStyle
                : "general";
        const sentences = draftText
            .split(/(?<=[.!?])\s+/)
            .map((s) => s.trim())
            .filter(Boolean);
        if (sentences.length < 2) {
            return `<ul><li>${escapeHtml(draftText)}</li></ul>`;
        }

        const cues = {
            present: (s) =>
                /\b(currently|right now|now|today|at the moment)\b/i.test(s),
            past: (s) =>
                /\b(previously|before|earlier|past|started|apprentice|rotating)\b/i.test(
                    s
                ),
            impact: (s) =>
                /%|\b(increased|reduced|saved|grew|impact|result|outcome|improved|delivered|launched|cut)\b/i.test(
                    s
                ),
            future: (s) =>
                /\b(next|future|looking ahead|looking to|want to|excited to|bring)\b/i.test(
                    s
                ),
            learning: (s) =>
                /\b(learned|realised|changed|reflect|lesson)\b/i.test(s),
            values: (s) => /\b(value|principle|care)\b/i.test(s),
            personality: (s) =>
                /\b(outside of work|outside work|hobby|into)\b/i.test(s),
        };

        const ordered = [];
        const used = new Set();
        const take = (fn) => {
            sentences.forEach((s, i) => {
                if (!used.has(i) && fn(s)) {
                    used.add(i);
                    ordered.push(s);
                }
            });
        };

        let grouped = null;
        if (style === "structured-summary") {
            const present = sentences.filter(
                (s, i) => cues.present(s) && !used.has(i)
            );
            present.forEach((s) => ordered.push(s));
            present.forEach((s, i) => used.add(sentences.indexOf(s, i)));

            const past = sentences.filter(
                (s, i) => cues.past(s) && !used.has(i)
            );
            past.forEach((s) => ordered.push(s));
            past.forEach((s, i) => used.add(sentences.indexOf(s, i)));

            const impact = sentences.filter(
                (s, i) => cues.impact(s) && !used.has(i)
            );
            impact.forEach((s) => ordered.push(s));
            impact.forEach((s, i) => used.add(sentences.indexOf(s, i)));

            const future = sentences.filter(
                (s, i) => cues.future(s) && !used.has(i)
            );
            future.forEach((s) => ordered.push(s));
            future.forEach((s, i) => used.add(sentences.indexOf(s, i)));

            grouped = [present, past, impact, future].filter((g) => g.length);
        } else if (style === "STAR") {
            take(cues.past);
            take((s) => !cues.impact(s));
            take(cues.impact);
        } else if (style === "reflection") {
            take((s) => !cues.learning(s));
            take(cues.learning);
            take(cues.impact);
        } else if (style === "value-alignment") {
            take(cues.values);
            take(cues.past);
            take(cues.impact);
            take(cues.future);
        }

        sentences.forEach((s, i) => {
            if (!used.has(i)) ordered.push(s);
        });

        const response =
            grouped && grouped.length
                ? grouped.map(
                      (lines) => `<li>${escapeHtml(lines.join(" "))}</li>`
                  )
                : ordered.map((line) => `<li>${escapeHtml(line)}</li>`);
        return `<ul>${response.join("")}</ul>`;
    }, [current, draftText, escapeHtml]);

    const handleDraftAssist = useCallback(() => {
        if (draftAssistApplied) {
            setBody(draftAssistPrevBody);
            setDraftAssistApplied(false);
            return;
        }
        if (!hasDraft || draftText.length < 200) return;
        setDraftAssistPrevBody(body || "");
        setBody(buildDraftResponse());
        setDraftAssistApplied(true);
    }, [
        body,
        buildDraftResponse,
        draftAssistApplied,
        draftAssistPrevBody,
        draftText.length,
        hasDraft,
        setBody,
    ]);

    const quillModules = useMemo(
        () => ({
            toolbar: [
                [{ font: [] }, { size: ["small", false, "large"] }],
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["clean"],
            ],
        }),
        []
    );

    const quillFormats = useMemo(
        () => ["font", "size", "bold", "italic", "underline", "list", "bullet"],
        []
    );

    const handlePlayback = (url) => {
        setPlaybackURL(url);
        setShowPlayback(true);
    };
    const handleDelete = (rec) => {
        if (rec?.id) deleteRecording(rec.id);
    };

    if (!current) return <p className="qd-no-questions">No questions found.</p>;

    const isFirst =
        all[0] && slug(all[0].id ?? all[0].question) === currentSlug;
    const isLast =
        all.length > 0 &&
        slug(all[all.length - 1].id ?? all[all.length - 1].question) ===
            currentSlug;
    const position =
        all.findIndex((q) => slug(q.id ?? q.question) === currentSlug) + 1;

    const blurActive = isBlurOn && !blurError && isBlurReady;
    const isEnablingBlur = isBlurOn && !blurError && !isBlurReady;
    const showBlurOverlay = Boolean(isEnablingBlur || blurError);

    // ── Recording modal (extracted for clarity) ───────────────────────────────
    const recordingModal = (
        <div
            className="rec-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Practice recording"
        >
            <div className="rec-modal__inner">
                <div className="rec-modal__context">
                    <span className="rec-modal__cat">
                        {current.category || "General"}
                    </span>
                    <p className="rec-modal__question">{current.question}</p>
                    {current.introduction && (
                        <div
                            className="rec-modal__intro"
                            dangerouslySetInnerHTML={{
                                __html: current.introduction,
                            }}
                        />
                    )}
                </div>

                <div className="rec-modal__camera-wrap">
                    <div className="rec-modal__card">
                        <div className="rec-modal__card-top">
                            <Timer isRunning={isRunning} />
                            <button
                                className="rec-modal__close"
                                onClick={safeCloseModal}
                                aria-label="Close recording"
                            >
                                <IconClose />
                            </button>
                        </div>

                        <div className="rec-modal__viewport">
                            {showNotes && (
                                <div
                                    className="rec-modal__notes-overlay"
                                    aria-label="Draft notes"
                                >
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                body ||
                                                "<span>No draft yet.</span>",
                                        }}
                                    />
                                </div>
                            )}
                            <video
                                className={`rec-modal__video${blurActive ? " is-hidden" : ""}`}
                                ref={modalVideoRef}
                                autoPlay
                                playsInline
                                muted
                            />
                            <canvas
                                className={`rec-modal__canvas${blurActive ? " is-active" : ""}`}
                                ref={blurCanvasRef}
                                aria-hidden="true"
                            />
                            {mediaError && (
                                <div className="rec-modal__media-error">
                                    {mediaError}
                                </div>
                            )}
                            {showBlurOverlay && (
                                <div
                                    className="rec-modal__blur-overlay"
                                    role="status"
                                    aria-live="polite"
                                >
                                    <div className="rec-modal__blur-card">
                                        {isEnablingBlur && (
                                            <div
                                                className="rec-modal__blur-spinner"
                                                aria-hidden="true"
                                            />
                                        )}
                                        <div className="rec-modal__blur-text">
                                            <p className="rec-modal__blur-title">
                                                {blurError
                                                    ? "Background blur unavailable"
                                                    : "Enabling background blur…"}
                                            </p>
                                            <p className="rec-modal__blur-body">
                                                {blurError
                                                    ? blurError
                                                    : "This can take a moment on some devices."}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {isRecording && (
                                <div
                                    className="rec-modal__rec-badge"
                                    aria-hidden="true"
                                >
                                    <span className="rec-modal__rec-dot" /> REC
                                </div>
                            )}
                        </div>

                        <div className="rec-modal__card-bottom">
                            <div className="rec-modal__controls">
                                <button
                                    type="button"
                                    className={`rec-modal__notes-btn${showNotes ? " is-active" : ""}`}
                                    onClick={() => setShowNotes((v) => !v)}
                                    aria-pressed={showNotes}
                                >
                                    {showNotes ? (
                                        <IconNotesOn />
                                    ) : (
                                        <IconNotesOff />
                                    )}
                                    {/* {showNotes ? "Hide notes" : "Show notes"} */}
                                </button>
                                {/* <button
                                    type="button"
                                    className={`rec-modal__blur-btn${isBlurOn ? " is-active" : ""}`}
                                    onClick={() => {
                                        setBlurError(null);
                                        setIsBlurOn((v) => !v);
                                    }}
                                    aria-pressed={isBlurOn}
                                >
                                    {isBlurOn ? (
                                        <IconBlurOn />
                                    ) : (
                                        <IconBlurOff />
                                    )}
                                    {isBlurOn ? "Blur on" : "Blur off"}
                                </button> */}
                            </div>
                            <button
                                type="button"
                                className={`rec-modal__rec-btn${isRecording ? " is-recording" : ""}`}
                                onClick={toggleRecording}
                                aria-pressed={isRecording}
                            >
                                <span
                                    className={`rec-modal__rec-dot-btn${isRecording ? " is-on" : ""}`}
                                    aria-hidden="true"
                                />
                                {isRecording
                                    ? "Stop recording"
                                    : "Start recording"}
                            </button>
                        </div>
                    </div>
                </div>

                {showProcessing && (
                    <div
                        className="rec-modal__processing"
                        role="status"
                        aria-live="polite"
                    >
                        <div className="rec-modal__processing-card">
                            {processingError ? (
                                <>
                                    <div
                                        className="rec-modal__processing-icon rec-modal__processing-icon--error"
                                        aria-hidden="true"
                                    >
                                        !
                                    </div>
                                    <h3 className="rec-modal__processing-title">
                                        Upload failed
                                    </h3>
                                    <p className="rec-modal__processing-body">
                                        {processingError}
                                    </p>
                                    <div className="rec-modal__processing-actions">
                                        <button
                                            type="button"
                                            className="rec-modal__action-btn rec-modal__action-btn--ghost"
                                            onClick={() => {
                                                setShowProcessing(false);
                                                setProcessingError(null);
                                            }}
                                        >
                                            Back to recording
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div
                                        className="rec-modal__spinner"
                                        aria-hidden="true"
                                    />
                                    <h3 className="rec-modal__processing-title">
                                        Processing your recording
                                    </h3>
                                    <p className="rec-modal__processing-body">
                                        {isProAiPlan
                                            ? "Your answer is being saved and feedback is on its way. You can leave this page safely."
                                            : "Your answer is being saved. Upgrade to Pro AI to receive detailed feedback."}
                                    </p>
                                    <div className="rec-modal__processing-actions">
                                        <button
                                            type="button"
                                            className="rec-modal__action-btn rec-modal__action-btn--primary"
                                            onClick={goRecordings}
                                        >
                                            View recordings <IconArrow />
                                        </button>
                                        <button
                                            type="button"
                                            className="rec-modal__action-btn rec-modal__action-btn--ghost"
                                            onClick={closeProcessing}
                                        >
                                            Stay here
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    // ── Playback modal (extracted for clarity) ────────────────────────────────
    const playbackModal = (
        <div
            className="pb-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Recording playback"
        >
            <div
                className="pb-modal__backdrop"
                onClick={() => setShowPlayback(false)}
                aria-hidden="true"
            />
            <div className="pb-modal__card">
                <div className="pb-modal__header">
                    <span className="pb-modal__title">Playback</span>
                    <button
                        className="pb-modal__close"
                        onClick={() => setShowPlayback(false)}
                        aria-label="Close playback"
                    >
                        <IconClose />
                    </button>
                </div>
                <div className="pb-modal__video-wrap">
                    <video
                        src={playbackURL}
                        controls
                        className="pb-modal__video"
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="qd">
            {/* ── Top bar ── */}
            <div className="qd__topbar">
                <div className="qd__pager">
                    <button
                        className="qd__pager-btn"
                        onClick={() => step(-1)}
                        disabled={isFirst}
                        aria-label="Previous question"
                    >
                        <IconChevLeft /> Prev
                    </button>
                    <span className="qd__pager-pos" aria-live="polite">
                        {position} / {all.length}
                    </span>
                    <button
                        className="qd__pager-btn"
                        onClick={() => step(1)}
                        disabled={isLast}
                        aria-label="Next question"
                    >
                        Next <IconChevRight />
                    </button>
                </div>
                <button
                    className="qd__practice-btn"
                    onClick={() => setShowModal(true)}
                    aria-haspopup="dialog"
                >
                    <IconVideo /> Practice now
                </button>
            </div>

            {/* ── Main layout ── */}
            <div className="qd__body">
                <section
                    className="qd__question-card"
                    aria-labelledby="qd-question-title"
                >
                    {current.category && (
                        <span className="qd__category">{current.category}</span>
                    )}
                    <h2 id="qd-question-title" className="qd__question-text">
                        {current.question}
                    </h2>
                    {current.introduction && (
                        <div
                            className="qd__intro"
                            dangerouslySetInnerHTML={{
                                __html: current.introduction,
                            }}
                        />
                    )}
                </section>

                <section className="qd__draft-card" aria-label="Draft answer">
                    <div className="qd__card-header">
                        <div className="qd__card-title-row">
                            <h3 className="qd__card-title">Draft answer</h3>
                            <p className="qd__card-hint">
                                Note key points before you record.
                            </p>
                        </div>
                        {!isEditingDraft && (
                            <button
                                type="button"
                                className="qd__edit-btn"
                                onClick={() => setIsEditingDraft(true)}
                            >
                                <IconEdit /> {hasDraft ? "Edit" : "Add draft"}
                            </button>
                        )}
                    </div>

                    {isEditingDraft ? (
                        <div className="qd__editor-wrap">
                            <ReactQuill
                                theme="snow"
                                value={body}
                                onChange={setBody}
                                modules={quillModules}
                                formats={quillFormats}
                                placeholder='e.g. "I led a cross-functional team of 5 to deliver…"'
                            />
                            <div className="qd__draft-meta" aria-live="polite">
                                <span className="qd__draft-status">
                                    {draftError ? (
                                        <span className="qd__draft-status--error">
                                            Could not save.{" "}
                                            <button
                                                type="button"
                                                className="qd__retry"
                                                onClick={saveNow}
                                            >
                                                Retry
                                            </button>
                                        </span>
                                    ) : saving ? (
                                        <span className="qd__draft-status--saving">
                                            Saving…
                                        </span>
                                    ) : lastSavedAt ? (
                                        <span className="qd__draft-status--saved">
                                            Saved{" "}
                                            {formatDateTimeNumeric(lastSavedAt)}
                                        </span>
                                    ) : (
                                        <span className="qd__draft-status--idle">
                                            Unsaved
                                        </span>
                                    )}
                                </span>
                                <div className="qd__draft-actions">
                                    {/* <button
                                        type="button"
                                        className="qd__btn-assist"
                                        onClick={handleDraftAssist}
                                        disabled={
                                            (!draftAssistApplied &&
                                                (!hasDraft ||
                                                    draftText.length < 200)) ||
                                            (draftAssistApplied &&
                                                !draftAssistPrevBody)
                                        }
                                    >
                                        {draftAssistApplied
                                            ? "Undo response"
                                            : "Draft assist"}
                                    </button> */}
                                    <button
                                        type="button"
                                        className="qd__btn-cancel"
                                        onClick={() => {
                                            setBody(lastSavedBody || "");
                                            setDraftAssistApplied(false);
                                            setDraftAssistPrevBody("");
                                            setIsEditingDraft(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="qd__btn-save"
                                        onClick={async () => {
                                            await saveNow();
                                            setDraftAssistApplied(false);
                                            setDraftAssistPrevBody("");
                                            setIsEditingDraft(false);
                                        }}
                                    >
                                        Save draft
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            className={`qd__draft-display${hasDraft ? "" : " qd__draft-display--empty"}`}
                            role="region"
                            aria-label="Draft content"
                            dangerouslySetInnerHTML={{
                                __html: hasDraft ? body : "",
                            }}
                        />
                    )}
                </section>

                <section
                    className="qd__recordings-card"
                    aria-label="Your recordings"
                >
                    <div className="qd__card-header">
                        <h3 className="qd__card-title">Recordings</h3>
                    </div>
                    <UserRecording
                        recordings={recordings}
                        loading={recordingsLoading}
                        questionSlug={currentSlug}
                        handlePlayback={handlePlayback}
                        handleDelete={handleDelete}
                        downloadToken={token}
                    />
                </section>
            </div>

            {/* ── Portalled modals — escape the dashboard stacking context ── */}
            {showModal &&
                portalTarget &&
                createPortal(recordingModal, portalTarget)}
            {showPlayback &&
                portalTarget &&
                createPortal(playbackModal, portalTarget)}
        </div>
    );
}
